import React from "react"
import { DataTable, SelectAsync, TextArea, Button, Input, Icon, Switch } from '@arnsbogroup/react-voicearchive'
import messages from "./messages"
import { useIntl, FormattedMessage } from 'react-intl'
import debounce from 'debounce-promise'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'
import { DataTableDateTime } from 'components/datatable'

const AddLiveSession = ({version, setVersion, editing = false}) => {
  const { formatMessage } = useIntl()
  const emptyLsDate = {
    dateStart: new Date(),
    dateEnd: new Date(),
    message: ''
  }
  
  const updateLsEntry = (key, entry, value) => {
    let newDates = version.liveSessionDates
    const index = newDates.findIndex(item => 
      item.dateStart == entry.dateStart &&
      item.dateEnd == entry.dateEnd &&
      item.message == entry.message
    )
    newDates[index] = ({...newDates[index], [key]: value})
    setVersion({...version, liveSessionDates: newDates})
  }

  const removeLiveSessionDate = (entry) => {
    
    let newDates = version.liveSessionDates
    const indexToRemove = newDates.findIndex(item => 
      item.dateStart == entry.dateStart &&
      item.dateEnd == entry.dateEnd &&
      item.message == entry.message
    )
    if (indexToRemove !== -1) {
      newDates.splice(indexToRemove, 1)
      setVersion((prev) => ({...prev, liveSessionDates: newDates}))
    }
  }

  const i18n = (errorMessages) => errorMessages?.map((errorMessage) => ({
    present: formatMessage(messages.errorsPresent)
  }[errorMessage] || errorMessage))
  
  const [liveSessionValidations, validateLiveSession] = useValidate({
    liveSessionCostCurrency: version.liveSessionCostCurrency.label,
    liveSessionPriceCurrency: version.liveSessionPriceCurrency.label,
  }, {
    liveSessionCostCurrency: [is.present],
    liveSessionPriceCurrency: [is.present]
  })

  return (
    <div>
      <div className="flex my-2 gap-2 p-2">
        <Switch
          label={formatMessage(messages.createLiveSessionSwitch)}
          onChange={() => setVersion({...version, createLiveSessionTask: !version.createLiveSessionTask})}
          checked={version.createLiveSessionTask}
          disabled={editing}
        />
      </div>
      {version.createLiveSessionTask && 
        <div className="w-full">
          <div className='flex flex-col-2 m-2'>
          <div className="flex w-full gap-2">
              <Input
                size="sm"
                label={formatMessage(messages.inputLiveSessionCostLabel)}
                type="number"
                value={version.liveSessionCost}
                onChange={({ target: { value } }) => setVersion({...version, liveSessionCost: value})}
                disabled={editing}
              />

              <SelectAsync
                required
                size="sm"
                label={formatMessage(messages.selectLiveSessionCostCurrencyLabel)}
                className="mt-12"
                value={[version.liveSessionCostCurrency]}
                onChange={(value) => setVersion({...version, liveSessionCostCurrency: value})}
                options={[]}
                cacheOptions
                defaultOptions
                errors={i18n(liveSessionValidations.liveSessionCostCurrency)}
                loadOptions={
                debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                  .then((response) => response.json())
                  .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                  , 250)
                }
                isDisabled={editing}
              />
            </div>
            <div className="flex items-center w-full gap-2">
              <Input
                size="sm"
                label={formatMessage(messages.inputLiveSessionPriceLabel)}
                type="number"
                value={version.liveSessionPrice}
                onChange={({ target: { value } }) => setVersion({...version, liveSessionPrice: value})}
                disabled={editing}
              />
              <SelectAsync
                required
                size="sm"
                label={formatMessage(messages.selectLiveSessionPriceCurrencyLabel)}
                className="mt-12"
                value={[version.liveSessionPriceCurrency]}
                onChange={(value) => setVersion({...version, liveSessionPriceCurrency: value})}
                options={[]}
                cacheOptions
                defaultOptions
                errors={i18n(liveSessionValidations.liveSessionPriceCurrency)}
                loadOptions={
                debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                  .then((response) => response.json())
                  .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                  , 250)
                }
                isDisabled={editing}
              />
            </div>
          </div>
          <DataTable
            noDataMessage={formatMessage(messages.noData)}
            head={{
              dateStart: { label: formatMessage(messages.liveSessionDatesFrom) },
              dateEnd: { label: formatMessage(messages.liveSessionDatesTo) },
              message: { label: formatMessage(messages.liveSessionDatesMessage) },
              action: {label: <Icon icon={['fas', 'ellipsis-v']} />, className: 'w-16'}
            }}
            body={{
              dateStart: (entry) => 
                <DataTableDateTime 
                  value={entry.dateStart}
                  onChange={(value) => updateLsEntry('dateStart', entry, value)}
                  disabled={editing}
                />,
              dateEnd: (entry) => 
                <DataTableDateTime 
                  value={entry.dateEnd}
                  onChange={(value) => updateLsEntry('dateEnd', entry, value)}
                  disabled={editing}
                />,
              message: (entry) => 
                <TextArea
                  size="sm"
                  rows={1}
                  placeholder={formatMessage(messages.liveSessionDatesMessage)}
                  defaultValue={ entry.message }
                  onBlur={({ target: { value } }) => {
                    updateLsEntry('message', entry, value)
                  }}
                  disabled={editing}
                />,
              action: (entry) => 
                <span className={editing ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => editing ? undefined : removeLiveSessionDate(entry)}>
                  <Icon className={editing ? 'text-gray-300' : 'text-negative'} icon={['fas', 'trash']} />
                </span>,
            }}
            entries={version.liveSessionDates}
          />

          <div className='mt-2 text-right p-2'>
            <Button
              size="xs"
              onClick={() =>
                setVersion((prev) => ({
                  ...prev,
                  liveSessionDates: [...prev.liveSessionDates, emptyLsDate],
                }))
              }
              disabled={editing}
            >
              <FormattedMessage {...messages.liveSessionAddDate} />
            </Button>
          </div>
        </div>
      }
    </div>
  )
}

export default AddLiveSession
